import React, { useState } from 'react';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import Tabs from '../../../components/tabs';
import Spinners from '../../../components/spinner';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import Tag from '../../../components/lozenge/tags';
import moment from 'moment';
import { DATE_FORMAT, DATE_FORMAT_WITH_TIME } from '../../../constants/common';
import { convertNumberWithCommas, setDashAsEmptyValue } from '../../../utils/global';
import { ACCEPT_DECIMAL } from '../../../utils/apiDecimalConfig';
import {
  TaxBracketAccordingValue,
  TaxBracketHeaders,
  TaxBracketValue,
} from '../../exercise/exercise.styled';
import { TotalContainer } from '../../transactions/transaction.styled';
import {
  useGetTransactionByIdForVendor,
  useGetTradeOrderTransactionByIdFromVendor,
  useGetTaxDetailsForVendorByTransactionId,
  useWealthlaneAwardApi,
  useWealthlaneAwardSettlementApi,
  useGetTransferAllocateDetailByTransactionId,
  useGetDisbursementDetailForBrokerTradeTool,
} from '../../../hooks';
import useGetAwardDetailsByIdForBrokerTool from '../../../hooks/get/useGetAwardDetailsByIdForBrokerTool';
import {
  ParticipantCard,
  TransactionDetail,
  TransactionDivider,
  TransactionSingleDetailBlock,
} from '../../transactions/transaction.styled';
import type { Wealthlane_AwardService_Dtos_Transactions_TransactionList } from '@wealthlane/api-services/wealthlane-award-service';

// --- Reusable helpers ---

const formatCurrencyVal = (val: number | undefined | null): string => {
  if (val == null) return '-';
  if (val === 0) return '$0';
  return '$' + String(convertNumberWithCommas(Number(val).toFixed(ACCEPT_DECIMAL), true));
};

const formatShares = (val: number | undefined | null): string => {
  if (val == null) return '-';
  if (val === 0) return '0';
  return String(convertNumberWithCommas(Number(val).toFixed(ACCEPT_DECIMAL), false));
};

const formatDate = (val: string | undefined | null, format = DATE_FORMAT): string =>
  val ? moment(val).format(format) : '-';

// --- Reusable detail field ---

interface DetailFieldProps {
  label: string;
  value: string;
  icon?: string;
  id?: string;
}

const DetailField: React.FC<DetailFieldProps> = ({ label, value, icon, id }) => (
  <TransactionSingleDetailBlock id={id}>
    {icon && <span className='material-symbols-outlined'>{icon}</span>}
    <div>
      <Heading variant='body-xs' title={label} className='detailLabel' />
      <Heading variant='body' title={value} color={COLORS.Black} />
    </div>
  </TransactionSingleDetailBlock>
);

interface TagFieldProps {
  label: string;
  tagTitle: string;
  variant: 'success' | 'error' | 'info';
  id?: string;
}

const TagField: React.FC<TagFieldProps> = ({ label, tagTitle, variant, id }) => (
  <TransactionSingleDetailBlock id={id}>
    <div>
      <Heading variant='body-xs' title={label} className='detailLabel' />
      {tagTitle && tagTitle !== '-' ? <Tag title={tagTitle} variant={variant} /> : '-'}
    </div>
  </TransactionSingleDetailBlock>
);

// --- Reusable tax table section ---

interface TaxSectionProps {
  title: string;
  taxDetails: any[] | undefined;
  totalRate: number;
  totalAmount: number;
  idPrefix: string;
}

const TaxSection: React.FC<TaxSectionProps> = ({
  title,
  taxDetails,
  totalRate,
  totalAmount,
  idPrefix,
}) => (
  <>
    <div id={`${idPrefix}Header`}>
      <Heading
        variant='h5'
        title={title}
        color={COLORS?.Gray}
        className='mb-[18px] hidden sm:block'
        id={`${idPrefix}Title`}
      />
    </div>
    {taxDetails && taxDetails.length > 0 ? (
      <>
        <TaxBracketAccordingValue id={`${idPrefix}Container`}>
          <TaxBracketHeaders className='grid grid-cols-12 gap-x-5' id={`${idPrefix}Headers`}>
            <div className='col-span-3'>
              <Heading variant='body-xs' title='Tax Name' color={COLORS.Gray} />
            </div>
            <div className='col-span-4'>
              <Heading variant='body-xs' title='Tax Rate (%)' color={COLORS.Gray} />
            </div>
            <div className='col-span-4'>
              <Heading variant='body-xs' title='Value ($)' color={COLORS.Gray} />
            </div>
          </TaxBracketHeaders>

          <div id={`${idPrefix}Rows`}>
            {taxDetails.map((item: any, index: number) => (
              <TaxBracketValue
                className='grid grid-cols-12 items-start gap-x-5'
                key={index}
                id={`${idPrefix}Row.${index}`}>
                <div className='col-span-3'>
                  <Heading
                    variant='body-xs'
                    title={item?.taxName || '-'}
                    color={COLORS.Gray}
                  />
                </div>
                <div className='col-span-4'>
                  <Heading
                    variant='body-xs'
                    title={
                      item?.taxRate
                        ? convertNumberWithCommas(Number(item.taxRate).toFixed(2), true)
                        : '-'
                    }
                    color={COLORS.Gray}
                  />
                </div>
                <div className='col-span-4'>
                  <Heading
                    variant='body-xs'
                    title={
                      item?.taxAmount
                        ? convertNumberWithCommas(Number(item.taxAmount).toFixed(2), true)
                        : '-'
                    }
                    color={COLORS.Gray}
                  />
                </div>
              </TaxBracketValue>
            ))}
          </div>
        </TaxBracketAccordingValue>

        <TotalContainer
          className='grid grid-cols-12 items-center gap-x-5 mb-8'
          id={`${idPrefix}Total`}>
          <div className='col-span-3'>
            <Heading variant='body-xs' title='Total Tax Amount' color={COLORS.Gray} />
          </div>
          <div className='col-span-4 flex pl-2'>
            <Heading
              variant='body'
              title={
                totalRate
                  ? convertNumberWithCommas(Number(totalRate).toFixed(ACCEPT_DECIMAL), true) + '%'
                  : '0%'
              }
              color={COLORS.Black}
            />
          </div>
          <div className='col-span-4 flex'>
            <Heading
              variant='body'
              title={
                totalAmount
                  ? '$' + convertNumberWithCommas(Number(totalAmount).toFixed(ACCEPT_DECIMAL), true)
                  : '$0'
              }
              color={COLORS.Black}
            />
          </div>
        </TotalContainer>
      </>
    ) : (
      <div className='flex justify-center mb-[18px]' id={`${idPrefix}NotAvailable`}>
        <Heading title='Applicable Tax not available' variant='h6' color={COLORS.Purple} />
      </div>
    )}
  </>
);

// --- Main component ---

interface ViewDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: Wealthlane_AwardService_Dtos_Transactions_TransactionList | null;
  companyId: string;
}

const ViewDetailsModal: React.FC<ViewDetailsModalProps> = ({ isOpen, onClose, row, companyId }) => {
  const [openTab, setOpenTab] = useState(0);
  const { handleRequest: awardHandleRequest } = useWealthlaneAwardApi();
  const { handleRequest: settlementHandleRequest } = useWealthlaneAwardSettlementApi();

  const transactionId = row?.transactionId ?? '';

  const { data: extraTransactionDetails, isLoading: txLoading } = useGetTransactionByIdForVendor(
    awardHandleRequest,
    companyId,
    transactionId
  );
  const { data: transferAndAllocateData, isLoading: transferAndAllocateLoading } =
    useGetTransferAllocateDetailByTransactionId(awardHandleRequest, companyId, transactionId);
  const { data: disbursementDetails, isLoading: disbursementDetailLoading } =
    useGetDisbursementDetailForBrokerTradeTool(settlementHandleRequest, transactionId, companyId);

  const [taxInitialValue, setTaxInitialValue] = useState<any>(null);
  const { isLoading: taxLoading } = useGetTaxDetailsForVendorByTransactionId(
    awardHandleRequest,
    companyId,
    transactionId,
    setTaxInitialValue
  );

  const sumField = (items: any[] | undefined, field: string): number =>
    items?.reduce((total: number, item: any) => total + (item[field] ?? 0), 0) ?? 0;

  const totalAdminTaxRate = sumField(taxInitialValue?.adminEstimatedTaxDetails, 'taxRate');
  const totalAdminTaxAmount = sumField(taxInitialValue?.adminEstimatedTaxDetails, 'taxAmount');
  const totalEstimatedTaxRate = sumField(taxInitialValue?.estimatedTaxDetails, 'taxRate');
  const totalEstimatedTaxAmount = sumField(taxInitialValue?.estimatedTaxDetails, 'taxAmount');

  const { data: brokerageDetails, isLoading: brokerageLoading } =
    useGetTradeOrderTransactionByIdFromVendor(settlementHandleRequest, transactionId, companyId);

  const awardId =
    (row as any)?.awardType === 'RSU'
      ? (row as any)?.rsuAwardVestindId
      : (row as any)?.awardType === 'PSU'
      ? (row as any)?.psuAwardVestindId
      : (row as any)?.optionAwardId;
  const awardType = (row as any)?.awardType || row?.recordType;
  const isRsuOrPsu = awardType === 'RSU' || awardType === 'PSU';

  const { data: awardDetails, isLoading: awardLoading } = useGetAwardDetailsByIdForBrokerTool(
    awardHandleRequest,
    awardId,
    awardType,
    companyId
  );

  if (!row) return null;

  const tx = extraTransactionDetails;

  const TransactionTab = () => (
    <>
      {txLoading ? (
        <Spinners />
      ) : (
        <ParticipantCard>
          <TransactionDetail id='transactionDetail'>
            <div id='transactionHeader'>
              <Heading
                variant='h5'
                title='Transaction Detail'
                color={COLORS?.Gray}
                className='hidden sm:block'
                id='transactionDetail'
              />
            </div>

            <div id='transactionDetailTag'>
              <Tag title='Transaction Detail' variant='info' />
            </div>
            <div className='grid grid-cols-2 lg:grid-cols-4 rowDetail' id='transactionDetailsGrid'>
              <DetailField
                label='Transaction Date'
                value={tx?.transactionCreatedDate || '-'}
                id='transactionDateDetails'
              />
              <DetailField
                label='Shares'
                value={formatShares(tx?.shares)}
                id='sharesDetails'
              />
              <DetailField
                label={isRsuOrPsu ? 'FMV at Release' : 'FMV at Exercise'}
                value={formatCurrencyVal(tx?.fmv)}
                id='fmvDetails'
              />
              <DetailField
                label={isRsuOrPsu ? 'Cost Per Share' : 'Exercise Price'}
                value={formatCurrencyVal(tx?.totalCostPrice)}
                id='costPerShareDetails'
              />
              <DetailField
                label='Income YTD'
                value={formatCurrencyVal(tx?.incomeYtd)}
                id='incomeYtdDetails'
              />
              <DetailField
                label={`${isRsuOrPsu ? 'Vesting' : 'Exercise'} Value`}
                value={formatCurrencyVal(row?.vestingValue)}
                id='vestingDetails'
              />
              <DetailField
                label='Acquisition Cost'
                value={isRsuOrPsu ? '-' : formatCurrencyVal(tx?.cost)}
                id='acquisitionCostDetails'
              />
              <DetailField
                label='Income Gain'
                value={formatCurrencyVal(tx?.incomeGain)}
                id='incomeGainDetails'
              />
              <DetailField
                label='Total Commission'
                value={formatCurrencyVal(tx?.totalCommision)}
                id='totalCommissionDetails'
              />
              <DetailField
                label='Total Fees'
                value={formatCurrencyVal(tx?.totalFees)}
                id='totalFeesDetails'
              />
              <DetailField
                label='Total Taxes'
                value={formatCurrencyVal(tx?.totalTaxes)}
                id='totalTaxesDetails'
              />
              <DetailField
                label='Total Taxes, Fees and Commissions'
                value={formatCurrencyVal(tx?.totalTaxesAndFeesAndCommission)}
                id='totalTaxes'
              />
              <DetailField
                label='Adj Cost Basis'
                value={formatCurrencyVal(tx?.adjustedCostBasisPrice)}
                id='adjCostBasisDetails'
              />
              <DetailField
                label='Funding Amount Required'
                value={formatCurrencyVal(tx?.fundingAmountRequired)}
                id='fundingAmountRequiredDetails'
              />
              <DetailField
                label='Prior Funding Amount'
                value={formatCurrencyVal(tx?.priorFundingAmount)}
                id='priorFundingAmountDetails'
              />
              <DetailField
                label='Total Funded Amount'
                value={formatCurrencyVal(tx?.totalFundedAmount)}
                id='totalFundedAmountDetails'
              />
              <TagField
                label='Tax Collection Status'
                tagTitle={row?.taxCollectionStatus || '-'}
                variant={row?.taxCollectionStatus === 'Failed' ? 'error' : 'info'}
                id='taxCollectionStatusDetails'
              />
              <DetailField
                label='Sell Price'
                value={formatCurrencyVal(tx?.sellPriceFromBoker)}
                id='sellPriceDetails'
              />
            </div>
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail' id='currentStockPriceGrid'>
              <DetailField
                label='Current Stock Price'
                value={formatCurrencyVal(tx?.currentStockPrice)}
                id='currentStockPriceDetails'
              />
            </div>
            <div className='grid grid-cols-2 lg:grid-cols-4 rowDetail' id='shareDetailsGrid'>
              <DetailField
                label='Sell Order Shares'
                value={formatShares(tx?.sellOrderShares)}
                id='sellOrderSharesDetails'
              />
              <DetailField
                label='Withhold Shares From Participant'
                value={formatShares(tx?.withheldSharesFromParticipant)}
                id='withholdSharesDetails'
              />
              <DetailField
                label='Transfer Shares Due Participant'
                value={
                  tx?.transferSharesDueParticipant
                    ? formatShares(tx.transferSharesDueParticipant)
                    : '-'
                }
                id='transferSharesDetails'
              />
              <DetailField
                label='Money Due Company'
                value={formatCurrencyVal(tx?.moneyDueCompany)}
                id='moneyDueCompanyDetails'
              />
              <DetailField
                label='Money Due Participant'
                value={formatCurrencyVal(tx?.moneyDueParticipant)}
                id='moneyDueParticipantDetails'
              />
              <DetailField
                label='Money Due Wealthlane'
                value={formatCurrencyVal(tx?.moneyDueWealthlane)}
                id='moneyDueWealthlaneDetails'
              />
              <TransactionSingleDetailBlock id='transactionStatusEmpty' />
              <DetailField
                label='Is External Transaction'
                value={tx?.isExternalTransaction === true ? 'Yes' : 'No'}
                id='isExternalTransactionDetails'
              />
              <DetailField
                label='Comment'
                value={tx?.comment || '-'}
                id='commentDetails'
              />
            </div>
            <div className='grid sm:grid-cols-2 lg:grid-cols-2 rowDetail'>
              <DetailField
                label='Shares Destination'
                value={tx?.sharesDestination || '-'}
                id='sharesDestinationDetails'
              />
              <DetailField
                label='Status Message'
                value={tx?.statusMessage || '-'}
                id='statusMessageDetails'
              />
            </div>

            <TransactionDivider id='transferAllocateDivider' />
            <Tag title='Transfer and Allocate Detail' variant='info' />
            <div className='grid grid-cols-2 lg:grid-cols-4 rowDetail'>
              <DetailField
                label='DWAC Number'
                value={transferAndAllocateData?.dwacNumber || '-'}
                id='dwacNumberDetails'
              />
              <DetailField
                label='Transfer Request Date'
                value={formatDate(transferAndAllocateData?.transferAllocateRequestedDate)}
                id='transferRequestDateDetails'
              />
              <DetailField
                label='Transfer Complete Date'
                value={formatDate(transferAndAllocateData?.transferAllocateCompletedDate)}
                id='transferCompleteDateDetails'
              />
              <TagField
                label='Transfer and Allocate Status'
                tagTitle={transferAndAllocateData?.containerStatus || '-'}
                variant='info'
                id='transferAllocateStatusDetails'
              />
              <DetailField
                label='Transfer Shares'
                value={
                  transferAndAllocateData?.allotedShare != null
                    ? transferAndAllocateData.allotedShare === 0
                      ? '0'
                      : String(
                          convertNumberWithCommas(
                            Number(transferAndAllocateData.allotedShare),
                            false
                          )
                        )
                    : '-'
                }
                id='transferSharesAmountDetails'
              />
            </div>

            <TransactionDivider id='disbursementDivider' />
            <div id='disbursementTag'>
              <Tag title='Disbursement Detail' variant='info' />
            </div>
            <div className='grid grid-cols-2 lg:grid-cols-4 rowDetail' id='disbursementGrid'>
              <DetailField
                label='Money Due Company'
                value={formatCurrencyVal(disbursementDetails?.moneyDueCompany)}
                id='disbursementMoneyDueCompany'
              />
              <DetailField
                label='Destination Account Number'
                value={disbursementDetails?.disbursementAccount || '-'}
                id='destinationAccountNumber'
              />
              <DetailField
                label='Disbursement Requested Date'
                value={formatDate(disbursementDetails?.disbursementRequestedDate)}
                id='disbursementRequestedDate'
              />
              <DetailField
                label='Disbursement Completed Date'
                value={formatDate(disbursementDetails?.disbursemenCompletedDate)}
                id='disbursementCompletedDate'
              />
              <TransactionSingleDetailBlock id='disbursementStatus'>
                <div>
                  <Heading
                    variant='body-xs'
                    title='Disbursement Status'
                    className='detailLabel'
                    id='disbursementStatusLabel'
                  />
                  {disbursementDetails?.disbursementItemStatus ? (
                    <Tag title={disbursementDetails.disbursementItemStatus} variant='info' />
                  ) : (
                    <Heading variant='body' title='-' color={COLORS.Black} />
                  )}
                </div>
              </TransactionSingleDetailBlock>
            </div>

            <TransactionDivider />
            <Tag title='Brokerage Detail' variant='info' />
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail'>
              <DetailField label='Broker Name' value={String(setDashAsEmptyValue(tx?.brokerName))} />
              <DetailField
                label='Broker Account Number'
                value={String(setDashAsEmptyValue(tx?.brokerAccountNumber))}
              />
              <DetailField
                label='Brokerage Account'
                value={String(setDashAsEmptyValue(tx?.brokerageAccount))}
              />
            </div>

            <TransactionDivider />
            <Tag title='Transfer Agent Detail' variant='info' />
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail'>
              <DetailField
                label='Transfer Agent Name'
                value={String(setDashAsEmptyValue(tx?.transferAgentName))}
              />
              <DetailField
                label='Transfer Agent Account Number'
                value={String(setDashAsEmptyValue(tx?.transferAgentAccountNumber))}
              />
            </div>
          </TransactionDetail>
        </ParticipantCard>
      )}
    </>
  );

  const TaxTab = () => (
    <>
      {taxLoading ? (
        <Spinners />
      ) : (
        <>
          <TaxSection
            title='Admin Estimated Tax Details'
            taxDetails={taxInitialValue?.adminEstimatedTaxDetails}
            totalRate={totalAdminTaxRate}
            totalAmount={totalAdminTaxAmount}
            idPrefix='adminTax'
          />
          <TaxSection
            title='Estimated Tax Details'
            taxDetails={taxInitialValue?.estimatedTaxDetails}
            totalRate={totalEstimatedTaxRate}
            totalAmount={totalEstimatedTaxAmount}
            idPrefix='estimatedTax'
          />
        </>
      )}
    </>
  );

  const BrokerageTab = () => (
    <>
      {brokerageLoading ? (
        <Spinners />
      ) : !brokerageDetails ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: COLORS.Gray }}>
          No brokerage details available.
        </div>
      ) : (
        <ParticipantCard>
          <TransactionDetail>
            <Heading variant='h5' title='Order Detail' color={COLORS?.Gray} />
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail'>
              <DetailField
                label='Broker Sell Type'
                value={brokerageDetails.brokerSellType || '-'}
                icon='share'
              />
              <DetailField
                label='Shares'
                value={formatShares(brokerageDetails.sellShare)}
                icon='share'
              />
              <DetailField
                label='Required Funds'
                value={formatCurrencyVal(brokerageDetails.fundingRequired)}
                icon='share'
              />
              <DetailField
                label='Created Date'
                value={formatDate(brokerageDetails.placedDate, DATE_FORMAT_WITH_TIME)}
                icon='calendar_month'
              />
            </div>

            <TransactionDivider />
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail'>
              <DetailField
                label='Sold Shares'
                value={formatShares(brokerageDetails.soldShare)}
                icon='share'
              />
              <DetailField
                label='Sell Price'
                value={formatCurrencyVal(brokerageDetails.sellPrice)}
                icon='payments'
              />
              <DetailField
                label='Sale Proceeds'
                value={formatCurrencyVal(brokerageDetails.sellProceeds)}
                icon='star'
              />
              <DetailField
                label='Fees'
                value={formatCurrencyVal(brokerageDetails.fees)}
                icon='payments'
              />
            </div>
            <TransactionDivider />
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail'>
              <DetailField
                label='Status'
                value={brokerageDetails.status || '-'}
                icon='bolt'
              />
              <DetailField
                label='Completed Date'
                value={formatDate(brokerageDetails.completedDate, DATE_FORMAT_WITH_TIME)}
                icon='calendar_month'
              />
              <DetailField
                label='Net Proceeds'
                value={formatCurrencyVal(brokerageDetails.netProceeds)}
                icon='width_full'
              />
              <DetailField
                label='Brokerage Order Number'
                value={brokerageDetails.orderNumber || '-'}
                icon='share'
              />
            </div>
          </TransactionDetail>
        </ParticipantCard>
      )}
    </>
  );

  const AwardTab = () => (
    <>
      {awardLoading ? (
        <Spinners />
      ) : !awardDetails ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: COLORS.Gray }}>
          No award details available.
        </div>
      ) : (
        <ParticipantCard>
          <TransactionDetail>
            <Heading variant='h5' title='Award Details' color={COLORS?.Gray} />
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 rowDetail'>
              <DetailField
                label='Award Number'
                value={awardDetails.awardNumber || '-'}
                icon='calendar_month'
              />
              <DetailField
                label={isRsuOrPsu ? 'Vest Units' : 'Exercise Units'}
                value={
                  'exerciseUnits' in awardDetails && awardDetails.exerciseUnits != null
                    ? formatShares(awardDetails.exerciseUnits)
                    : formatShares(awardDetails?.grantQuantity)
                }
                icon='star'
              />
              <DetailField
                label={isRsuOrPsu ? 'Vest Date' : 'Exercise Date'}
                value={
                  isRsuOrPsu
                    ? formatDate((awardDetails as any)?.vestingDate)
                    : formatDate((row as any)?.exerciseDate)
                }
                icon='calendar_month'
              />
              <DetailField
                label='Grant Date'
                value={formatDate(awardDetails?.grantIssueDate)}
                icon='calendar_month'
              />
            </div>
            <TransactionDivider />
            <div className='grid sm:grid-cols-2 lg:grid-cols-3 rowDetail'>
              <DetailField
                label='Grant Name'
                value={awardDetails.grantName || '-'}
                icon='star'
              />
              <DetailField
                label='Grant Type'
                value={awardDetails.awardType || '-'}
                icon='share'
              />
              <DetailField
                label='Plan Name'
                value={awardDetails.planName || '-'}
                icon='star'
              />
            </div>
          </TransactionDetail>
        </ParticipantCard>
      )}
    </>
  );

  const isLongShareSell = row?.recordType === 'Long Share Sell';

  const items = [
    ...(!isLongShareSell ? [{ name: 'Award', content: <AwardTab /> }] : []),
    { name: 'Transaction', content: <TransactionTab /> },
    ...(!isLongShareSell ? [{ name: 'Tax', content: <TaxTab /> }] : []),
    { name: 'Brokerage', content: <BrokerageTab /> },
  ];

  return (
    <ModalComponent
      show={isOpen}
      closeModal={onClose}
      title='Transaction Details'
      size='xxl'
      id='viewDetailsModal'>
      <div>
        <ModalBodyComponent>
          <Tabs
            className='generic-tab-align'
            hasTabContent
            tabIndex={openTab}
            setTabIndex={(index) => setOpenTab(index)}
            items={items}
          />
        </ModalBodyComponent>
        <ModalFooterComponent
          loading={false}
          onCloseModal={onClose}
          modalStatus='View'
          isEditShow={false}
          confirmValues={false}
          valueChanged={false}
          id='viewDetailsFooter'
        />
      </div>
    </ModalComponent>
  );
};

export default ViewDetailsModal;
